import { Vehicle, VehicleChargeSchedule } from '@/types'
import { VehicleTrip } from '@/reducers/trips'
import {
  convertTimeTo12HourFormat,
  getNextChargeDate,
  getNextDepartureDate,
  isTodayOrTomorrow,
  toLongTimeString,
} from '@/utils/date'
import { ChargeScheduleDaysOfWeek } from '@/types/chargeSchedule'
import { isAfter, isBefore } from 'date-fns'
import { isTesla } from '@/utils/vehicles/car'
import { canCharge } from '@/utils/vehicles/permissions'
import type { Props } from '@/app/components/AlertMessage'
import { sanitizeHtmlContent } from '@/utils/components'

import {
  teslaMissingPermissions,
  teslaMissingVirtualKey,
} from '@/utils/vehicles/car/permissions'
import { TeslaCar } from '@/types/vehicle'

function generateGoalsText(chargeSchedule?: VehicleChargeSchedule) {
  const plans = []
  if (!chargeSchedule) {
    return 'No goals selected'
  }

  if (chargeSchedule.save_the_planet_plan_enabled) {
    plans.push('Save the Planet')
  }
  if (chargeSchedule.money_saving_plan_enabled) {
    plans.push('Save Money')
  }
  if (chargeSchedule.solar_surplus_plan_enabled) {
    plans.push('Solar Surplus')
  }

  if (plans.length === 0) {
    plans.push('No goals selected')
  }

  return plans.join(', ')
}

function generateTripsText(trips?: VehicleTrip[]) {
  const upcomingTrips = trips?.filter((trip) => {
    const date = new Date(trip.departure_datetime_utc)
    const now = new Date()

    return now < date
  })
  if (!upcomingTrips || upcomingTrips.length === 0) {
    return 'No upcoming trips planned for this vehicle'
  }
  const nextTrip = upcomingTrips[0]

  const dateStr = toLongTimeString(nextTrip.departure_datetime_utc)
  return `Next: Hit ${nextTrip.battery_target_max}% on ${dateStr}`
}

// This is done in support of adding the next Day of Week to the schedule subtitle
// By converting the string day to a number we can easily figure out when the next
// scheduled day of week will be
const sortUpcomingSchedules = (
  schedules: ChargeScheduleDaysOfWeek[],
  property: 'departure_time' | 'charging_start_time',
) => {
  return schedules
    .filter((charge) => !!charge[property])
    .map((charge) => {
      if (charge.day_of_week == null) {
        return { ...charge, startDate: null }
      }

      if (property === 'departure_time') {
        return { ...charge, startDate: getNextDepartureDate(charge) }
      }
      if (property === 'charging_start_time') {
        return { ...charge, startDate: getNextChargeDate(charge) }
      }
    })
    .sort((a, b) => {
      if (!a || !a.startDate) {
        return -1
      }
      if (!b || !b.startDate) {
        return 1
      }

      return isAfter(a.startDate, b.startDate) ? 1 : -1
    })
}

function generateScheduleSubtitle(chargeSchedule?: VehicleChargeSchedule) {
  if (
    !chargeSchedule ||
    chargeSchedule.charge_schedule_days_of_week.length === 0
  ) {
    return 'No scheduled departure/charging planned'
  }

  const departures = sortUpcomingSchedules(
    chargeSchedule.charge_schedule_days_of_week,
    'departure_time',
  )

  const scheduledCharges = sortUpcomingSchedules(
    chargeSchedule.charge_schedule_days_of_week,
    'charging_start_time',
  )

  const today = new Date()
  const nextDeparture = departures.find((dep) => {
    if (!dep || !dep.startDate) {
      return false
    }
    return isBefore(today, dep.startDate) && isTodayOrTomorrow(dep.startDate)
  })

  const nextCharge = scheduledCharges.find((charge) => {
    if (!charge || !charge.startDate) {
      return false
    }

    if (nextDeparture && nextDeparture.startDate) {
      return (
        isBefore(charge.startDate, nextDeparture.startDate) &&
        isBefore(today, charge.startDate) &&
        isTodayOrTomorrow(charge.startDate)
      )
    }

    return (
      isBefore(today, charge.startDate) && isTodayOrTomorrow(charge.startDate)
    )
  })

  const strings = []

  if (chargeSchedule.schedule_start_charge_enabled && nextCharge) {
    const time = convertTimeTo12HourFormat(nextCharge.charging_start_time)
    strings.push(`Start at ${time}`)
  }

  if (chargeSchedule.schedule_departure_enabled && nextDeparture) {
    const time = convertTimeTo12HourFormat(nextDeparture.departure_time)
    strings.push(`Depart at ${time}`)
  }

  if (strings.length === 0) {
    return 'No scheduled departure/charging planned'
  }

  const joinedStrings = strings.join(', ')
  return `Next: ${joinedStrings}`
}

type Alert = Pick<Props, 'variant'> & {
  message: string
  link?: {
    text: string
    urlOrPath: string
  }
}

function isTeslaMissingRequiredPermissions(
  vehicle: Vehicle<TeslaCar>,
): boolean {
  const isVehicleMissingVirtualKey = teslaMissingVirtualKey(vehicle.car)
  const isVehicleMissingVehicleInfo = teslaMissingPermissions(
    vehicle.car,
    'vehicle_device_data',
  )
  const isVehicleMissingChargeControl = teslaMissingPermissions(
    vehicle.car,
    'vehicle_charging_cmds',
  )
  return (
    isVehicleMissingVehicleInfo ||
    isVehicleMissingVirtualKey ||
    isVehicleMissingChargeControl
  )
}

function getMissingPermissions(vehicle: Vehicle<TeslaCar>): string[] {
  const missingPermissions = []
  if (teslaMissingVirtualKey(vehicle.car)) {
    missingPermissions.push('Virtual Key')
  }
  if (teslaMissingPermissions(vehicle.car, 'vehicle_device_data')) {
    missingPermissions.push('Vehicle Information')
  }
  if (teslaMissingPermissions(vehicle.car, 'vehicle_charging_cmds')) {
    missingPermissions.push('Vehicle Charge Management')
  }
  return missingPermissions
}

function getAlert(
  utilityAlert: string | '',
  vehicle?: Vehicle,
  chargeSchedule?: VehicleChargeSchedule,
): Alert | undefined {
  const userEnabledChargeControl =
    chargeSchedule?.allow_charging_control ?? false
  if (utilityAlert) {
    return {
      variant: 'info',
      message: sanitizeHtmlContent(utilityAlert),
    }
  }
  if (!vehicle) return undefined

  if (isTesla(vehicle) && vehicle.requires_tesla_fleet_migration) {
    if (vehicle.car?.tesla_command_protocol_disabled) {
      return {
        variant: 'error',
        message: 'We can’t control your charge settings.',
        link: {
          text: 'Upgrade Your Connection Now',
          urlOrPath: `/connect-device/vehicle/tesla/permissions-statement`,
        },
      }
    }
    return {
      variant: 'warning',
      message:
        'Tesla will soon stop supporting the way you’ve connected your car to Optiwatt.',
      link: {
        text: 'Upgrade Your Connection Now',
        urlOrPath: `/connect-device/vehicle/tesla/permissions-statement`,
      },
    }
  }

  if (isTesla(vehicle) && isTeslaMissingRequiredPermissions(vehicle)) {
    const missingPermissions = getMissingPermissions(vehicle)
    const missingPermissionsText = missingPermissions.join(', ')
    if (userEnabledChargeControl) {
      return {
        variant: 'warning',
        message: `We can’t control your Tesla due to a missing capability. (${missingPermissionsText})<br>Disable charge control or click the link below.`,
        link: {
          text: 'Fix This Now',
          urlOrPath: `/connect-device/vehicle/tesla?missing-capabilities=true`,
        },
      }
    }
    return {
      variant: 'info',
      message: `We can't control your Tesla due to a missing capability. (${missingPermissionsText})`,
      link: {
        text: 'Fix This Now',
        urlOrPath: `/connect-device/vehicle/tesla?missing-capabilities=true`,
      },
    }
  }
  if (!vehicleCanCharge(vehicle)) {
    return {
      variant: 'info',
      message:
        'This vehicle does not currently support third-party charge management and functionality will be limited.',
    }
  }
  return undefined
}

const vehicleCanCharge = (vehicle: Vehicle | undefined): boolean => {
  if (!vehicle) {
    return false
  }
  if (isTesla(vehicle)) {
    return (
      !vehicle.car?.public_key_not_attached_at &&
      !vehicle.car?.tesla_command_protocol_disabled &&
      canCharge(vehicle)
    )
  }

  return canCharge(vehicle)
}

function getConnectionText(vehicle: Vehicle | undefined): string {
  if (!vehicle) {
    return ''
  }

  if (isTesla(vehicle)) {
    // if the vehicle has no public key, show that first because
    // we need that for any sort of data or control
    if (vehicle.car?.public_key_not_attached_at) {
      return 'Pair your Virtual Key'
    }
    // if permissions are the problem
    else if (!canCharge(vehicle)) {
      return 'Enable Permissions'
    }
  }

  return 'Connect EVSE'
}

export default {
  getAlert,
  generateGoalsText,
  generateTripsText,
  generateScheduleSubtitle,
  getConnectionText,
  vehicleCanCharge,
}
